import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from 'src/contexts/SessionContext';
import { Platform } from 'src/models/platform/_types';
import { fetchAllPlatforms } from 'src/models/platform/platform';
import { platformLabel } from 'src/models/platform/utils';
import {
  PlatformNode,
  fetchPlatformNodes,
  platformNodesQueryKey,
} from 'src/models/platform-node/platform-node';
import { getHqAllowedLocationIds } from 'src/shared/HQ/utils';
import { Identifier } from 'src/types/utilityTypes';

import { BaseSelect } from './BaseSelect';

interface SiteSelectProps {
  values: any[];
  onChange: (value: string | string[]) => void;
  showMyLocationsOnly?: boolean; // Restrict options to user's assigned locations
  excludeSites?: Identifier[];
  platformsOnly?: boolean; // Show only platforms, exclude hierarchy nodes
  label?: string; // Override the default label
}

const SiteSelect: React.FC<SiteSelectProps> = ({
  values,
  onChange,
  showMyLocationsOnly = false,
  excludeSites = [],
  platformsOnly = false,
  label,
}) => {
  const { session, trainer } = useSession();
  const { t } = useTranslation('translation', {
    keyPrefix: 'sites-select',
  });

  const { data: platforms, isLoading } = useQuery({
    queryKey: ['platforms'],
    queryFn: async () => {
      const response = await fetchAllPlatforms();

      return response || [];
    },
    enabled: !!session?.id,
    staleTime: 60000,
    gcTime: 60000,
  });
  const { data: nodes = { nodes: [], total: 0 } } = useQuery({
    queryKey: [platformNodesQueryKey],

    queryFn: () =>
      fetchPlatformNodes({
        includeHierarchyNodes: true,
        fetchAll: true,
      }),

    enabled: !!session?.id,
  });

  const [groupedNodesAndPlatforms, setGroupedNodesAndPlatforms] = useState<
    Record<string, (PlatformNode | Platform)[]>
  >({});

  useEffect(() => {
    if (nodes?.nodes && platforms) {
      // Compute HQ-restricted IDs if showMyLocationsOnly is enabled
      let allowedIds: string[] | undefined;
      if (showMyLocationsOnly && trainer) {
        allowedIds = getHqAllowedLocationIds(trainer, platforms, nodes.nodes);
      }

      // Filter by allowed IDs and exclude specified sites
      const isAllowed = (item: PlatformNode | Platform): boolean => {
        const idStr = String((item as any).id);
        const idNum = Number(idStr);
        // First check if site is excluded
        if (excludeSites.includes(idStr) || excludeSites.includes(idNum)) {
          return false;
        }

        // Then check if restriction is enabled
        if (!allowedIds || allowedIds.length === 0) return true;
        // Nodes in options are represented as `${id} node`, so allow either form
        return (
          allowedIds.includes(idStr) || allowedIds.includes(`${idStr} node`)
        );
      };

      const filteredNodes = platformsOnly ? [] : nodes.nodes.filter(isAllowed);
      const filteredPlatforms = platforms.filter(isAllowed);

      // No debug logging in production version

      const finalNodes = filteredNodes.sort((a, b) => {
        return (a.hierarchyNode?.level || 0) - (b.hierarchyNode?.level || 0);
      });
      const grouped = [...finalNodes, ...filteredPlatforms].reduce(
        (acc, node) => {
          let type;
          if ('trainerName' in node) {
            type = 'Site';
          } else if ('name' in node) {
            type = node.hierarchyNode?.name;
          }
          if (!acc[type]) {
            acc[type] = [];
          }
          acc[type].push(node);
          return acc;
        },
        {} as Record<string, (PlatformNode | Platform)[]>
      );
      setGroupedNodesAndPlatforms(grouped);
    }
  }, [
    nodes,
    platforms,
    showMyLocationsOnly,
    trainer,
    excludeSites,
    platformsOnly,
  ]);

  const options = [
    ...Object.entries(groupedNodesAndPlatforms).flatMap(
      ([groupName, items]) => [
        { id: groupName, label: groupName, value: '', disabled: true },
        ...items.map((item) => ({
          id: 'trainerName' in item ? `${item.id}` : `${item.id} node`,
          label:
            'trainerName' in item
              ? platformLabel(item) || 'Unknown Platform'
              : item.name || 'Unknown Node',
          value: item,
        })),
      ]
    ),
  ];

  // Custom filter function that preserves section headers during search
  const customFilterOptions = (
    options: any[],
    { inputValue }: { inputValue: string }
  ) => {
    if (!inputValue) return options;

    const filteredOptions: any[] = [];
    let currentGroup: any = null;

    for (const option of options) {
      if (option.disabled) {
        currentGroup = option;
      } else {
        const matches = option.label
          .toLowerCase()
          .includes(inputValue.toLowerCase());
        if (matches) {
          if (currentGroup) {
            filteredOptions.push(currentGroup);
            currentGroup = null;
          }
          filteredOptions.push(option);
        }
      }
    }

    return filteredOptions;
  };
  return (
    <BaseSelect
      label={label || t('label')}
      placeholder={t('placeholder')}
      value={values}
      valueOnly={false}
      onChange={(selected) => {
        onChange(selected.map((option) => option.id));
      }}
      options={options}
      loading={isLoading}
      multiple={true}
      filterOptions={customFilterOptions}
    />
  );
};

export default SiteSelect;
